import { useCallback, useMemo } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import ScrapTrackingViewportPlot from "components/scrapTracking/ScrapTrackingViewportPlot";
import type { PlotMouseEvent } from "plotly.js";
import type { TrainableWindow } from "src/store/api/generatedApi";
import { useResolvedScrapViewportTimeZone } from "store/slices/scrapTrackingViewportSlice";
import {
  buildStackedBarTraces,
  formatHoverTimeRange,
  stackedChartLayout,
  type StackedBarTrace,
  timelineChartConfig,
  TimelineLegend,
} from "../components/timelineChartUtils";

type TrainablePeriodsTimelineProps = Readonly<{
  windows: readonly TrainableWindow[];
  start: Date;
  end: Date;
  onWindowClick?: (window: TrainableWindow) => void;
}>;

const TRAINABLE_COLOR = "#00897b";
const REVIEWED_COLOR = "#4caf50";
const REVIEWED_ONLY_COLOR = "#ff9800";
const EMBEDDED_ONLY_COLOR = "#2196f3";
const LABELLED_COLOR = "#ffc107";
const TRANSFORMS_COLOR = "#9c27b0";
const VIDEO_ONLY_COLOR = "#81d4fa";
const VIDEO_NOT_LABELLED_COLOR = "#a5d6a7";
const NO_COVERAGE_COLOR = "#e0e0e0";

interface TrainableBands {
  trainable: number;
  reviewSurplus: number;
  embeddingSurplus: number;
  noCoverage: number;
}

const computeTrainableBands = (w: TrainableWindow): TrainableBands => {
  const review = w.review_coverage_ratio;
  const embedding = w.min_embedding_coverage_ratio;
  return {
    trainable: Math.min(review, embedding),
    reviewSurplus: Math.max(0, review - embedding),
    embeddingSurplus: Math.max(0, embedding - review),
    noCoverage: 1.0 - Math.max(review, embedding),
  };
};

const TRAINABLE_STAGE_CONFIG = [
  { key: "trainable" as const, color: TRAINABLE_COLOR },
  { key: "reviewSurplus" as const, color: REVIEWED_ONLY_COLOR },
  { key: "embeddingSurplus" as const, color: EMBEDDED_ONLY_COLOR },
  { key: "noCoverage" as const, color: NO_COVERAGE_COLOR },
] as const;

interface VideoPipelineBands {
  embedding: number;
  transform: number;
  video: number;
  noCoverage: number;
}

const computeVideoPipelineBands = (w: TrainableWindow): VideoPipelineBands => {
  const effVideo = w.min_video_coverage_ratio;
  const effTransform = Math.min(effVideo, w.min_transform_coverage_ratio);
  const effEmbedding = Math.min(effTransform, w.min_embedding_coverage_ratio);
  return {
    embedding: effEmbedding,
    transform: effTransform - effEmbedding,
    video: effVideo - effTransform,
    noCoverage: 1.0 - effVideo,
  };
};

const VIDEO_PIPELINE_STAGE_CONFIG = [
  { key: "embedding" as const, color: EMBEDDED_ONLY_COLOR },
  { key: "transform" as const, color: TRANSFORMS_COLOR },
  { key: "video" as const, color: VIDEO_ONLY_COLOR },
  { key: "noCoverage" as const, color: NO_COVERAGE_COLOR },
] as const;

interface LabellingPipelineBands {
  review: number;
  labelling: number;
  video: number;
  noCoverage: number;
}

const computeLabellingPipelineBands = (
  w: TrainableWindow
): LabellingPipelineBands => {
  const effVideo = w.min_video_coverage_ratio;
  const effLabelling = Math.min(effVideo, w.labelling_coverage_ratio);
  const effReview = Math.min(effLabelling, w.review_coverage_ratio);
  return {
    review: effReview,
    labelling: effLabelling - effReview,
    video: effVideo - effLabelling,
    noCoverage: 1.0 - effVideo,
  };
};

const LABELLING_PIPELINE_STAGE_CONFIG = [
  { key: "review" as const, color: REVIEWED_COLOR },
  { key: "labelling" as const, color: LABELLED_COLOR },
  { key: "video" as const, color: VIDEO_NOT_LABELLED_COLOR },
  { key: "noCoverage" as const, color: NO_COVERAGE_COLOR },
] as const;

const trainableHoverText = (
  window: TrainableWindow,
  clickHint: string,
  timeZone: string
): string => {
  const lines = [
    `<b>${formatHoverTimeRange(window.window_start, window.window_end, timeZone)}</b>`,
    `Review: ${(window.review_coverage_ratio * 100).toFixed(1)}%`,
    `Embedding (worst angle): ${(window.min_embedding_coverage_ratio * 100).toFixed(1)}%`,
    `<span style="color:#4441C8"><i>${clickHint}</i></span>`,
  ];
  return lines.join("<br>");
};

const videoPipelineHoverText = (
  window: TrainableWindow,
  clickHint: string,
  timeZone: string
): string => {
  const lines = [
    `<b>${formatHoverTimeRange(window.window_start, window.window_end, timeZone)}</b>`,
    `Video (worst angle): ${(window.min_video_coverage_ratio * 100).toFixed(1)}%`,
    `Transform (worst angle): ${(window.min_transform_coverage_ratio * 100).toFixed(1)}%`,
    `Embedding (worst angle): ${(window.min_embedding_coverage_ratio * 100).toFixed(1)}%`,
    `<span style="color:#4441C8"><i>${clickHint}</i></span>`,
  ];
  return lines.join("<br>");
};

const labellingPipelineHoverText = (
  window: TrainableWindow,
  clickHint: string,
  timeZone: string
): string => {
  const lines = [
    `<b>${formatHoverTimeRange(window.window_start, window.window_end, timeZone)}</b>`,
    `Video (worst angle): ${(window.min_video_coverage_ratio * 100).toFixed(1)}%`,
    `Labelling: ${(window.labelling_coverage_ratio * 100).toFixed(1)}%`,
    `Review: ${(window.review_coverage_ratio * 100).toFixed(1)}%`,
    `<span style="color:#4441C8"><i>${clickHint}</i></span>`,
  ];
  return lines.join("<br>");
};

export const TrainablePeriodsTimeline = ({
  windows,
  start,
  end,
  onWindowClick,
}: TrainablePeriodsTimelineProps) => {
  const { t } = useTenantTranslation();
  const timeZone = useResolvedScrapViewportTimeZone();
  const theme = useTheme();

  const clickHint = t("modelCoverageClickToView");
  const lineColor = theme.palette.background.default;
  const trainableTraces = useMemo(
    () =>
      buildStackedBarTraces({
        windows,
        stageConfig: TRAINABLE_STAGE_CONFIG,
        computeBands: computeTrainableBands,
        hoverTextFn: (window) =>
          trainableHoverText(window, clickHint, timeZone),
        lineColor,
      }),
    [windows, clickHint, timeZone, lineColor]
  );
  const videoPipelineTraces = useMemo(
    () =>
      buildStackedBarTraces({
        windows,
        stageConfig: VIDEO_PIPELINE_STAGE_CONFIG,
        computeBands: computeVideoPipelineBands,
        hoverTextFn: (window) =>
          videoPipelineHoverText(window, clickHint, timeZone),
        lineColor,
      }),
    [windows, clickHint, timeZone, lineColor]
  );
  const labellingPipelineTraces = useMemo(
    () =>
      buildStackedBarTraces({
        windows,
        stageConfig: LABELLING_PIPELINE_STAGE_CONFIG,
        computeBands: computeLabellingPipelineBands,
        hoverTextFn: (window) =>
          labellingPipelineHoverText(window, clickHint, timeZone),
        lineColor,
      }),
    [windows, clickHint, timeZone, lineColor]
  );
  const layout = useMemo(
    () => stackedChartLayout(start, end, theme),
    [start, end, theme]
  );

  return (
    <Stack spacing={2}>
      <ChartSection
        title={t("trainablePeriodsTitle")}
        legends={[
          { color: TRAINABLE_COLOR, label: t("trainable") },
          {
            color: REVIEWED_ONLY_COLOR,
            label: t("trainablePeriodsReviewedOnly"),
          },
          {
            color: EMBEDDED_ONLY_COLOR,
            label: t("trainablePeriodsEmbeddedOnly"),
          },
          { color: NO_COVERAGE_COLOR, label: t("trainablePeriodsNeither") },
        ]}
        traces={trainableTraces}
        layout={layout}
        windows={windows}
        onWindowClick={onWindowClick}
      />

      <ChartSection
        title={t("trainablePeriodsVideoPipeline")}
        legends={[
          {
            color: EMBEDDED_ONLY_COLOR,
            label: t("trainablePeriodsEmbedded"),
          },
          {
            color: TRANSFORMS_COLOR,
            label: t("trainablePeriodsTransformsOnly"),
          },
          { color: VIDEO_ONLY_COLOR, label: t("trainablePeriodsVideoOnly") },
          { color: NO_COVERAGE_COLOR, label: t("trainablePeriodsNeither") },
        ]}
        traces={videoPipelineTraces}
        layout={layout}
        windows={windows}
        onWindowClick={onWindowClick}
      />

      <ChartSection
        title={t("trainablePeriodsLabellingPipeline")}
        legends={[
          { color: REVIEWED_COLOR, label: t("trainablePeriodsReviewed") },
          {
            color: LABELLED_COLOR,
            label: t("trainablePeriodsLabelledNotReviewed"),
          },
          {
            color: VIDEO_NOT_LABELLED_COLOR,
            label: t("trainablePeriodsVideoNotLabelled"),
          },
          { color: NO_COVERAGE_COLOR, label: t("trainablePeriodsNeither") },
        ]}
        traces={labellingPipelineTraces}
        layout={layout}
        windows={windows}
        onWindowClick={onWindowClick}
      />
    </Stack>
  );
};

type ChartSectionProps = Readonly<{
  title: string;
  legends: readonly { color: string; label: string }[];
  traces: StackedBarTrace[];
  layout: ReturnType<typeof stackedChartLayout>;
  windows: readonly TrainableWindow[];
  onWindowClick?: (window: TrainableWindow) => void;
}>;

const ChartSection = ({
  title,
  legends,
  traces,
  layout,
  windows,
  onWindowClick,
}: ChartSectionProps) => {
  const handleClick = useCallback(
    (event: Readonly<PlotMouseEvent>) => {
      const pointNumber = event.points[0]?.pointNumber;
      if (pointNumber === undefined || !onWindowClick) return;
      const clicked = windows[pointNumber];
      if (clicked) onWindowClick(clicked);
    },
    [windows, onWindowClick]
  );

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="subtitle2">{title}</Typography>
        {legends.map(({ color, label }) => (
          <TimelineLegend key={label} color={color} label={label} />
        ))}
      </Stack>
      <Box sx={{ width: "100%" }}>
        <ScrapTrackingViewportPlot
          data={traces}
          layout={layout}
          config={timelineChartConfig}
          style={{ width: "100%", cursor: "pointer" }}
          onClick={handleClick}
        />
      </Box>
    </Stack>
  );
};
